import { useRef } from 'react';
import type { ReelDraft } from '@/lib/reel';
import { parseReelCaptions, type ReelCaption } from '@/lib/reel-captions';
import { errorMessage } from '@/lib/api';
export function ReelCaptions({
  draft,
  duration,
  selected,
  onSelect,
  onChange,
  onError,
  disabled,
}: {
  draft: ReelDraft;
  duration: number;
  selected: string | null;
  onSelect: (id: string) => void;
  onChange: (draft: ReelDraft) => void;
  onError: (message: string) => void;
  disabled: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const add = (captions: ReelCaption[]) => {
    if (draft.layers.length + captions.length > 50)
      throw new Error('Use at most 50 text items. Remove an item before importing more captions.');
    const added = captions.map((c) => ({ ...c, id: crypto.randomUUID() }));
    onChange({
      ...draft,
      layers: [
        ...draft.layers,
        ...added.map((c) => ({
          id: c.id,
          kind: 'text' as const,
          text: c.text,
          x: 0.1,
          y: 0.72,
          w: 0.8,
          size: 0.055,
          rotate: 0,
          font: 'sans' as const,
          color: '#ffffff',
          align: 'center' as const,
          pill: '#111111',
          shade: false,
        })),
      ],
      layerTimings: [
        ...(draft.layerTimings ?? []),
        ...added.map((c) => ({ layerId: c.id, startMs: c.startMs, endMs: c.endMs })),
      ],
    });
    if (added[0]) onSelect(added[0].id);
  };
  const layer = draft.layers.find((l) => l.id === selected && l.kind === 'text');
  return (
    <fieldset className="picture-tools" disabled={disabled}>
      <strong>Captions</strong>
      <span className="composer-hint">
        Add your words or import an SRT file. Review and edit captions before Done.
      </span>
      <div className="picture-row">
        <button
          className="chip hv"
          type="button"
          disabled={draft.layers.length >= 50}
          onClick={() => add([{ text: 'Your caption', startMs: 0, endMs: duration }])}
        >
          Add caption
        </button>
        <button className="chip hv" type="button" onClick={() => input.current?.click()}>
          Import SRT
        </button>
        <input
          hidden
          ref={input}
          type="file"
          accept=".srt,text/plain,application/x-subrip"
          aria-label="Import SRT"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) {
              if (file.size > 100000) {
                onError('Choose an SRT file under 100 KB.');
                return;
              }
              void file
                .text()
                .then((text) => add(parseReelCaptions(text, duration)))
                .catch((error) => onError(errorMessage(error)));
            }
          }}
        />
      </div>
      <div className="reel-caption-list">
        {draft.layers
          .filter((l) => l.kind === 'text')
          .map((l) => (
            <button
              className="chip hv"
              type="button"
              key={l.id}
              aria-pressed={selected === l.id}
              onClick={() => onSelect(l.id)}
            >
              {l.text || 'Empty text'}
            </button>
          ))}
      </div>
      {layer?.kind === 'text' && (
        <>
          <label>
            Caption text
            <textarea
              aria-label="Caption text"
              maxLength={300}
              value={layer.text}
              onChange={(e) =>
                onChange({
                  ...draft,
                  layers: draft.layers.map((l) =>
                    l.id === layer.id ? { ...layer, text: e.target.value } : l,
                  ),
                })
              }
            />
          </label>
          <button
            className="chip hv"
            type="button"
            onClick={() =>
              onChange({
                ...draft,
                layers: draft.layers.filter((l) => l.id !== selected),
                layerTimings: (draft.layerTimings ?? []).filter((t) => t.layerId !== selected),
              })
            }
          >
            Remove caption
          </button>
        </>
      )}
    </fieldset>
  );
}
